/* Sticky sale bar: rotating sentences, live countdown, automatic discount. */
class PromoBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.promo-bar__slide'));
    this.rotateMs = (parseFloat(this.dataset.rotateSeconds) || 3) * 1000;
    this.index = 0;

    if (this.classList.contains('promo-bar--sticky') && this.parentElement) {
      this.parentElement.classList.add('is-sticky');
    }

    this.trackHeight();
    this.startCountdown();
    if (this.slides.length > 1) this.startRotation();
    this.applyDiscount();
  }

  disconnectedCallback() {
    this.stopRotation();
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }

  /* The header is pinned below this bar, so its offset has to follow the real height. */
  trackHeight() {
    const set = () => {
      document.documentElement.style.setProperty(
        '--promo-bar-height',
        `${this.getBoundingClientRect().height}px`
      );
    };
    set();

    // The header is pinned below this bar, so the offset must follow the bar's real
    // height — which changes when the merchant edits the text size, when a web font
    // finishes loading, or when the viewport reflows. Don't rely on any single signal.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(set);
      this.resizeObserver.observe(this);
    }
    window.addEventListener('resize', set);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(set);
    requestAnimationFrame(set);
  }

  /* Rotation */

  startRotation() {
    this.stopRotation();
    this.timer = setInterval(() => this.advance(), this.rotateMs);

    // Don't burn cycles rotating a bar nobody is looking at.
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopRotation();
      } else if (!this.timer && this.slides.length > 1) {
        this.timer = setInterval(() => this.advance(), this.rotateMs);
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  stopRotation() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  advance() {
    const current = this.slides[this.index];
    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');

    this.index = (this.index + 1) % this.slides.length;

    const next = this.slides[this.index];
    next.classList.add('is-active');
    next.removeAttribute('aria-hidden');
  }

  /* Countdown */

  startCountdown() {
    this.countdownSlide = this.querySelector('[data-countdown-target]');
    if (!this.countdownSlide) return;

    const seconds = parseInt(this.countdownSlide.dataset.countdownTarget, 10);
    if (!seconds) return;

    this.targetMs = seconds * 1000;
    this.units = {
      days: this.countdownSlide.querySelector('[data-unit="days"]'),
      hours: this.countdownSlide.querySelector('[data-unit="hours"]'),
      minutes: this.countdownSlide.querySelector('[data-unit="minutes"]'),
      seconds: this.countdownSlide.querySelector('[data-unit="seconds"]'),
    };
    this.daysWrap = this.countdownSlide.querySelector('[data-unit-wrap="days"]');
    this.srText = this.countdownSlide.querySelector('[data-countdown-sr]');

    this.tick();
    this.countdownTimer = setInterval(() => this.tick(), 1000);
  }

  tick() {
    const remaining = this.targetMs - Date.now();
    if (remaining <= 0) {
      this.expire();
      return;
    }

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor(remaining / 3600000) % 24;
    const minutes = Math.floor(remaining / 60000) % 60;
    const seconds = Math.floor(remaining / 1000) % 60;

    const pad = (n) => String(n).padStart(2, '0');
    this.units.days.textContent = pad(days);
    this.units.hours.textContent = pad(hours);
    this.units.minutes.textContent = pad(minutes);
    this.units.seconds.textContent = pad(seconds);

    if (this.daysWrap) this.daysWrap.hidden = days === 0;

    // Announce whole minutes only — a per-second live region is unusable.
    if (this.srText && seconds === 0) {
      this.srText.textContent = `${days} days ${hours} hours ${minutes} minutes remaining`;
    }
  }

  /* The sale is over: take the bar down and stop offsetting the header. */
  expire() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.stopRotation();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
    document.documentElement.style.setProperty('--promo-bar-height', '0px');
    if (this.parentElement) this.parentElement.classList.remove('is-sticky');
    this.remove();
  }

  /* Discount */

  applyDiscount() {
    const code = (this.dataset.discount || '').trim();
    if (!code) return;

    const key = `promoBarDiscount:${code}`;
    let alreadyApplied = false;
    try {
      alreadyApplied = window.sessionStorage.getItem(key) === '1';
    } catch (error) {
      // Private mode / storage disabled: fall through and just apply again.
    }
    if (alreadyApplied) return;

    // Hitting /discount/<CODE> is what tells Shopify to carry the code into the
    // cart and checkout. Following it in the background avoids a visible reload.
    fetch(`${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(() => {
        try {
          window.sessionStorage.setItem(key, '1');
        } catch (error) {
          /* not fatal */
        }
      })
      .catch(() => {
        /* Never let a failed discount call break the page. */
      });
  }
}

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar);
}
